<template>
    <div v-if="site && site.name" class="site container-fluid">
        <BetterLink v-can.someOf="'sites.edit,sites.suggest'" 
            class="float-right btn btn-sm btn-outline-primary text-dark" 
            :to="{name: 'Edit Site', params: {id: site.slug}}"
        >
            <span v-can="'sites.edit'">Edit</span>
            <span v-cannot="'sites.edit'">Add Info or Correct Errors</span>
        </BetterLink>
        
        <h2 class="h1 mb-0">{{ site.name }}</h2>
        <div class="row">
            <div class="col">
                <ul class="list-unstyled list-inline">
                   <li class="list-inline-item mr-4"><address>{{ site.location.formatted_locality }}</address></li>
                   <li v-if="Object.keys(site.average_ratings).length" class="list-inline-item mr-4"><StarRating :rating="combinedAverageRating(site.average_ratings)" :number-of-ratings="site.number_of_ratings" /></li>
                </ul>
            </div>
            <div class="col-md col-sm-7">
                <ul class="list-unstyled list-inline text-center text-sm-right">
                    <!-- <li class="list-inline-item ml-4"><a href="#" class="action-button"><fa-icon :icon="['fal', 'share-square']" class="mr-2" />Share</a></li> -->
                    <li class="list-inline-item mr-3 ml-sm-3">
                        <a href="#" @click.prevent="visitLocation(site, loadSite)" class="action-button visit" v-if="!site.is_visited"><fa-icon :icon="['fal', 'water']" class="mr-2" />Never Been</a>
                        <a href="#" @click.prevent="unvisitLocation(site, loadSite)" class="action-button visit text-info" v-if="site.is_visited"><fa-icon :icon="['fal', 'water']" class="mr-2" />Been Here</a>
                    </li>
                    <li class="list-inline-item mr-3 ml-sm-3">
                        <a href="#" @click.prevent="favLocation(site, loadSite)" class="action-button save" v-if="!site.is_faved"><fa-icon :icon="['fal', 'heart']" class="mr-2" />Favorite</a>
                        <a href="#" @click.prevent="unfavLocation(site, loadSite)" class="action-button save text-danger" v-if="site.is_faved"><fa-icon :icon="['fas', 'heart']" class="mr-2" />Favorite</a>
                    </li>
                </ul>
            </div>
        </div>
        
        <HeaderGallery v-if="coverPhotos.length" :images="coverPhotos" class="cover-photos mb-4" >
            <template v-slot:action-button v-if="site.website">
                <a :href="site.website" target="_blank" rel="noopener" class="btn btn-lg btn-secondary">Visit Website</a>
            </template>
        </HeaderGallery>
        <div v-else-if="site.videos.length" v-html="responsiveVideoHtml" class="mb-5" style="max-width: 2800px; margin-left: auto; margin-right: auto; text-align:center; border-radius: 24px; overflow: hidden;"></div>
        
        <div v-if="!site.coming_soon" class="highlighted-attributes d-none d-md-flex justify-content-center align-items-center mb-4">
            <ul class="list-unstyled list-inline text-center">
               <li class="list-inline-item">{{ site.operation }}</li>
               <li class="list-inline-item" v-if="site.min_price > 0">{{ formattedLowestPrice(site) }}</li>
               <li class="list-inline-item" v-if="site.number_of_pools > 0">{{ site.number_of_pools | withDeclensionalWord(' pool', ' pools') }}</li>
               <li class="list-inline-item" v-if="site.min_temp > 0">{{ temperatureRange(site) }}</li>
               <li class="list-inline-item">{{ site.attire || 'Unknown Attire' }}</li>
               <li class="list-inline-item" v-if="site.access">{{ site.access }}</li>
               <li class="list-inline-item" v-if="hasLodging(site)">Has Lodging</li>
            </ul>
        </div>
        
        <div v-if="site.alert_text" :class="`alert alert-${site.alert_style} my-5`" v-html="$autoP(site.alert_text)"></div>
        
        <div v-if="site.coming_soon" class="d-flex align-items-center justify-content-center" style="height:30vh">
            <h3 class="h2">We don't have much information about {{ site.name }} yet, but don't worry, we're working as fast as we can.</h3>
        </div>
        
        <div class="row">
            <div class="col-sm-7 col-xl-9 order-sm-1 order-xs-2">
                <template v-if="site.description || site.short_description">
                    <h3>Description</h3>
                    <p v-if="site.private_property" class="text-danger"><small><strong>This hot spring is on private property. Public access may not be allowed.</strong></small></p>
                    <div v-html="$autoP(site.description || site.short_description)"></div>
                </template>
                
                <template v-if="site.average_environmental_attributes">
                    <h3>Environment</h3>
                    <EnvironmentalAttributes :attributes="site.average_environmental_attributes" class="mb-3" />
                </template>
            </div>
                
            <div class="col-sm order-sm-2 order-xs-1">
                <template v-if="site.alternate_names">
                    <h3>Alternative names</h3>
                    <p>{{ site.alternate_names }}</p>
                </template>
                
                <div class="mb-3" v-if="site.hours && Object.keys(site.hours).length">
                    <h3>Hours</h3>
                    <HourChart :hours="site.hours" :abbreviate="true" />
                    <small v-if="hoursUpdatedAt(site) != false">Hours updated {{ hoursUpdatedAt(site) }}</small>
                </div>
                
                <template v-if="site.average_ratings && Object.keys(site.average_ratings).length">
                    <h3>Rating <small v-if="site.number_of_ratings > 0"> ({{ site.number_of_ratings | withDeclensionalWord(' user', ' users') }})</small></h3>
                    <RatingChart :ratings="site.average_ratings" />
                </template>
                <BetterLink v-if="!userProfile || !userProfile.id" class="btn btn-link text-primary p-0 mb-3" :to="{ name: 'Login', query: { redirect: this.$route.fullPath, continue: 'true' } }">Rate this Hot Spring</BetterLink>
                <button v-else-if="userHasRated" class="btn btn-link text-primary p-0 mb-3 text-small" v-b-modal.rating-modal>Update My Rating</button>
                <button v-else class="btn btn-link text-primary p-0 mb-3" v-b-modal.rating-modal>Rate this Hot Spring</button>
            </div>
        </div>
        
        <h3>Info</h3>
        <div class="row mb-3">
            <div class="col-lg-3 col-md-4 col-6 mb-2">
                <fa-icon :icon="['fal', 'comment-alt-lines']" class="mr-2 text-teal" fixed-width />{{ site.operation }}
            </div>
            <div class="col-lg-3 col-md-4 col-6 mb-2" v-if="site.min_price > 0">
                <fa-icon :icon="['fal', 'credit-card']" class="mr-2 text-teal" fixed-width />{{ priceRange(site) }}
            </div>
            <div class="col-lg-3 col-md-4 col-6 mb-2" v-if="site.number_of_pools > 0">
                <fa-icon :icon="['fal', 'swimming-pool']" class="mr-2 text-teal" fixed-width />{{ site.number_of_pools | withDeclensionalWord(' pool', ' pools') }}
            </div>
            <div class="col-lg-3 col-md-4 col-6 mb-2" v-if="site.min_temp > 0">
                <fa-icon :icon="['fal', 'thermometer-full']" class="mr-2 text-teal" fixed-width />{{ temperatureRange(site) }}
            </div>
            <div class="col-lg-3 col-md-4 col-6 mb-2">
                <fa-icon :icon="['fal', 'leaf-oak']" class="mr-2 text-teal" fixed-width />{{ site.attire || 'Unknown Attire' }}
            </div>
            <div class="col-lg-3 col-md-4 col-6 mb-2" v-if="site.access">
                <fa-icon :icon="['fal', 'car']" class="mr-2 text-teal" fixed-width />{{ site.access }}
            </div>
            <div class="col-lg-3 col-md-4 col-6 mb-2" v-if="site.allows_kids">
                <fa-icon :icon="['fal', 'baby']" class="mr-2 text-teal" fixed-width />Allows Kids
            </div>
            <div class="col-lg-3 col-md-4 col-6 mb-2" v-for="amenity in site.amenities" :key="amenity.id">
                <fa-icon :icon="['fal', amenity.icon]" class="mr-2 text-teal" fixed-width />{{ amenity.title }}
            </div>
        </div>
        
        <template v-if="site.rules">
            <h3>Rules / Etiquette</h3>
            <div v-html="$autoP(site.rules)"></div>
        </template>

        <template v-if="site.location && site.location.region">
            <h3>Location</h3>
            <p class="text-muted" v-if="site.location.address_1">
                {{ site.location.formatted_address }}
                <span class="ml-2 mr-2">•</span> 
                <a :href="directionsLink(site)" target="_blank">Get Directions</a>
            </p>
            
            <MglMap v-if="site.location && site.location.coords"
                    :accessToken="accessToken" 
                    :mapStyle="mapStyle" 
                    :center="mapboxCoords"
                    :interactive="false"
                    :zoom="10"
                    :pitch="100"
                    style="height:30vh; border-radius:24px; overflow: hidden;"
                    class="mb-4">
                <MglMarker :coordinates="mapboxCoords" color="#FF7964" />
            </MglMap>
        </template>
        
        <template v-if="site.how_to_access">
            <h3>How to Access</h3>
            <div v-html="$autoP(site.how_to_access)"></div>
        </template>
        
        <h3>Visit Reports</h3>
        <div class="reviews" v-if="site.reviews && site.reviews.length">
            <ol class="list-unstyled">
                <SiteReview v-for="review in sortedReviews" :key="review.id" :review="review" class="media mb-4"></SiteReview>
            </ol>
        </div>
        <p>Have you been to {{ site.name }}? Why not <a href="#" @click.prevent="presentReviewModal">leave a report</a>?</p>
        
        <div v-if="site.related_sites && site.related_sites.length">
            <hr class="my-5">
            <h3 class="mt-0">Other Hot Springs You Might Enjoy</h3>
            <ol class="list-unstyled row related-sites mb-5">
                <li class="col-xs-12 col-sm-6 col-lg-4 col-xl-3 mb-4" v-for="relatedSite in site.related_sites" :key="relatedSite.id">
                    <SmallSiteTile :tile-data="relatedSite" />
                </li>
            </ol>
        </div>
        
        <small><em>This site is provided as is and for informational purposes only. It may be out of date or incorrect. If you see something wrong, <BetterLink to="/contact">let us know</BetterLink>.</em></small>
        
        <b-modal id="rating-modal" :title="`Rate ${site.name}`" @show="showRatingModal" @cancel="cancelRatingModal" @close="cancelRatingModal" @ok="saveRating">
            <p>Please only rate hot springs that you have visited and have meaningful knowledge of. Abusive ratings will be removed.</p>
            <RatingChart :editable="true" :ratings="site.my_ratings" @change="ratingChanged" />
        </b-modal>
        
        <SiteReviewModal ref="review-modal" :site="site" :active="showReviewModal" @change="reviewAdded" />
    </div>
</template>

<script>
import axios from 'axios'
import SiteMixins from '@/mixins/SiteMixins';
import CurrencyMixins from '@/mixins/CurrencyMixins';
import HeaderGallery from "@/components/page-blocks/HeaderGallery"
import SiteReview from "@/components/elements/SiteReview"
import EnvironmentalAttributes from "@/components/elements/EnvironmentalAttributes"
import StarRating from "@/components/elements/StarRating"
import HourChart from "@/components/elements/HourChart"
import RatingChart from "@/components/elements/RatingChart"
import SmallSiteTile from "@/components/tiles/SmallSiteTile"
import SiteReviewModal from "@/components/modals/SiteReview"
import Mapbox from "mapbox-gl"
import { MglMap, MglMarker } from "vue-mapbox" 
import { mapState } from 'vuex'
import 'mapbox-gl/dist/mapbox-gl.css'

export default {
    name: 'Site',
    head: {
        title() {
            return {
                inner: this.site && this.site.name ? `${this.site.name} • ${this.site.location.formatted_locality}` : 'Site',
            }
        },
        meta() {
            const baseUrl = location.protocol+'//'+location.hostname
            const title = this.site && this.site.name ? `${this.site.name} • ${this.site.location.formatted_locality}` : 'drench. - Find Hot Springs'
            const imageAlt = this.site && this.site.name ? `Photo of ${this.site.name}` : 'Photo from drench. - Find Hot Springs'
            const link = this.site && this.site.slug ? `${baseUrl}/sites/${this.site.slug}` : baseUrl
            const description = this.site && this.site.name ? (this.site.short_description || this.site.description) : 'The best directory of hot springs.'
            const twitterParams = '?w=1200&h=600&fit=crop&crop=faces,entropy'
            const twitterImage = this.site && this.site.photos.length ? 'https:' + this.site.photos[0].permalink + twitterParams : `https://drench.imgix.net/media/drench-site-no-image.jpg${twitterParams}`
            const facebookParams = '?w=1200&w=630&fit=crop&crop=faces,entropy'
            const facebookImage = this.site && this.site.photos.length ? 'https:' + this.site.photos[0].permalink + facebookParams : `https://drench.imgix.net/media/drench-site-no-image.jpg${facebookParams}`
            
            return [
                { id: 'twitter:card', name: 'twitter:card', content: 'summary_large_image' },
                { id: 'twitter:site', name: 'twitter:site', content: title },
                { id: 'twitter:title', name: 'twitter:title', content: title },
                { id: 'twitter:description', name: 'twitter:description', content: description },
                { id: 'twitter:image:src', name: 'twitter:image:src', content: twitterImage },
                
                { id: 'og:title', name: 'og:title', content: title, 'xmlns:og': 'http://opengraphprotocol.org/schema/' },
                { id: 'og:url', name: 'og:url', content: link, 'xmlns:og': 'http://opengraphprotocol.org/schema/' },
                { id: 'og:description', name: 'og:description', content: description, 'xmlns:og': 'http://opengraphprotocol.org/schema/' },
                { id: 'og:image', name: 'og:image', content: facebookImage, 'xmlns:og': 'http://opengraphprotocol.org/schema/' },
                { id: 'og:image:width', name: 'og:image:width', content: '1200', 'xmlns:og': 'http://opengraphprotocol.org/schema/' },
                { id: 'og:image:height', name: 'og:image:height', content: '630', 'xmlns:og': 'http://opengraphprotocol.org/schema/' },
                { id: 'og:image:alt', name: 'og:image:alt', content: imageAlt, 'xmlns:og': 'http://opengraphprotocol.org/schema/' },
                { id: 'og:site_name', name: 'og:site_name', content: 'drench.', 'xmlns:og': 'http://opengraphprotocol.org/schema/' },
                { id: 'og:type', name: 'og:type', content: 'website', 'xmlns:og': 'http://opengraphprotocol.org/schema/' },
                { id: 'fb:app_id', name: 'fb:app_id', content: '371976654036508', 'xmlns:og': 'http://www.facebook.com/2008/fbml' },
            ]
        },
    },
    mixins: [
        SiteMixins,
        CurrencyMixins,
    ],
    components: {
        HeaderGallery,
        EnvironmentalAttributes,
        StarRating,
        HourChart,
        RatingChart,
        SmallSiteTile,
        MglMap,
        MglMarker,
        SiteReviewModal,
        SiteReview,
    },
    data() {
        return {
            site: null,
            accessToken: process.env.VUE_APP_MAPBOX_ACCESS_TOKEN,
            mapStyle: process.env.VUE_APP_MAPBOX_MAP_STYLE,
            showReviewModal: false,
        }
    },
    computed: {
        ...mapState('account', ['userProfile']),
        coverPhotos() {
            return this.site.photos ? this.site.photos.map(photo => {
                return {
                    permalink: photo.permalink,
                    params: photo.params,
                    oembed: photo.oembed,
                }
            }) : []
        },
        mapboxCoords() {
            return this.site && this.site.location && this.site.location.coords && [this.site.location.coords.lon, this.site.location.coords.lat] || [0,0]
        },
        userHasRated() {
            return this.site.my_ratings 
                && (this.site.my_ratings.access
                || this.site.my_ratings.beauty
                || this.site.my_ratings.comfort
                || this.site.my_ratings.safety)
        },
        sortedReviews() {
            return [...this.site.reviews].sort((a, b) => {
                if (a.visit_date == b.visit_date) return 0;
                return a.visit_date > b.visit_date ? -1 : 1;
            })
        },
        responsiveVideoHtml() {
            if (this.site.videos.length) {
                let video = this.site.videos[0].oembed
                let re = /src="([^"]+)"/i
                let matches = re.exec(video.html)
                let videoUrl = matches[1]
                // let paddingBottom = video.height / video.width * 100

                return `<style>.embed-container { position: relative; padding-bottom: 60vh; height: 0; overflow: hidden; max-width: 100%; } .embed-container iframe, .embed-container object, .embed-container embed { position: absolute; top: 0; left: 0; width: 100%; height: 100%; }</style><div class='embed-container'><iframe src='${videoUrl}' frameborder='0' allowfullscreen></iframe></div>`
            }
            return '';
        },
    },
    methods: {
        loadSite(siteData) {
            if (!siteData || !siteData.error) {
                let loader = this.$loading.show();
                axios({
                    url: process.env.VUE_APP_API_BASE_URL + '/sites/' + this.$route.params.id,
                    method: 'GET',
                }).then(response => {
                    this.site = response.data.data;
                    this.$emit('updateHead')
                    this.gaTrackScreenView(this.site.name)
                    loader.hide()
                }).catch(error => {
                    if (error.response.status == 404) {
                        this.$router.replace('/404')
                    }
                    loader.hide()
                    console.log({ error: error })
                })
            } else {
                if (siteData.error.response.status == 403) {
                    this.$router.push({ name: 'Login', query: { redirect: this.$route.fullPath, continue: 'true' } });
                }
            }
        },
        permalink(tile) {
            return '/sites/'+tile.slug
        },
        showRatingModal() {
            this.gaTrackInteraction('Show Rating Modal', this.site.name)
        },
        cancelRatingModal() {
            this.gaTrackInteraction('Cancel Rating Modal', this.site.name)
        },
        ratingChanged(key, rating) {
            this.site.my_ratings[key] = rating
        },
        saveRating() {
            this.gaTrackInteraction('Save Rating', this.site.name)
            let loader = this.$loading.show();
            axios({
                url: process.env.VUE_APP_API_BASE_URL + '/sites/'+this.$route.params.id+'/rate',
                method: 'PUT',
                data: {
                    my_ratings: this.site.my_ratings,
                },
            }).then(response => {
                this.site = response.data.data;
                loader.hide()
            }).catch(error => {
                loader.hide()
                console.log({ error: error })
            })
        },
        reviewAdded() {
            this.loadSite()
        },
        presentReviewModal() {
            if (!this.userProfile || !this.userProfile.id) {
                this.$router.push({ name: 'Login', query: { redirect: this.$route.fullPath, continue: 'true' } })
            } else {
                this.$refs['review-modal'].show()
            }
        },
    },
    created() {
        this.mapbox = Mapbox
        
        this.loadSite()
    },
};
</script>

<style lang="scss">
.site {
    address {
        font-size: 17px;
        margin-bottom: 0;
    }
    .action-button {
        color: $dark-gray;
        text-decoration: none;
    }
    .highlighted-attributes {
        ul {
            border: 1px solid lightgray;
            padding: 10px 30px;
            border-radius: 24px;
            display: inline-block;
            margin: 0;
            
            li {
                margin: 0;
                
                &:not(:last-child):after {
                    content: "•";
                    margin: 0 16px;
                }
            }
        }
    }
}
</style>